import { Component, type ErrorInfo, type ReactNode } from 'react'
import { Button } from './button'
import { ErrorState } from './feedback'

interface Props {
  children: ReactNode
  /** Shown instead of the default panel. */
  fallback?: ReactNode
}

interface State {
  error: Error | null
}

/**
 * Stops one broken component from taking down the page.
 *
 * This exists because it already happened: a misconfigured editor extension
 * threw inside the rich text field and white-screened the entire dashboard,
 * with nothing on screen to explain it. A club editor hitting that mid-article
 * would have no idea whether their work was saved. Now the rest of the form
 * survives and there is a visible way out.
 *
 * Error boundaries have no hook equivalent — a class is the only option. To
 * clear a caught error, give the boundary a `key` that changes (the route path,
 * usually): React remounts it, which is simpler and less surprising than
 * resetting state from a lifecycle method.
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null }

  static getDerivedStateFromError(error: Error): State {
    return { error }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('[ui] component crashed:', error, info.componentStack)
  }

  render() {
    if (!this.state.error) return this.props.children
    if (this.props.fallback) return this.props.fallback

    return (
      <ErrorState
        title="Something on this screen broke"
        description={
          this.state.error.message || 'An unexpected error stopped this part of the page.'
        }
        action={
          <Button variant="outline" onClick={() => window.location.reload()}>
            Reload the page
          </Button>
        }
      />
    )
  }
}
